// Wave 4.x — Personal recipient groups manager (Notifications → Groups tab).
// Each user builds their OWN named sets of teammates so a whole group can be
// dropped into the compose "To" bar in one click. Groups are PRIVATE to their
// owner (never shared across the company).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Plus, Pencil, Trash2, Loader2, UsersRound } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import RecipientPicker from '@/components/mail/RecipientPicker';

const ROLE_LABEL = { super_admin: 'Super Admin', company_admin: 'Company Admin', sales_person: 'Sales', sales_manager: 'Sales Mgr', user: 'User' };

export const MailGroupsManager = () => {
  const { effectiveCompanyId } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; object = edit
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/mail/groups', {
        params: effectiveCompanyId ? { company_id: effectiveCompanyId } : {},
      });
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setMembers([]);
    setEditorOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setName(g.name || '');
    setMembers((g.members || []).map((m) => ({ id: m.id, name: m.name, email: m.email, role: m.role })));
    setEditorOpen(true);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Give the group a name'); return; }
    if (members.length === 0) { toast.error('Add at least one member'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        member_ids: members.map((m) => m.id),
        company_id: effectiveCompanyId || null,
      };
      if (editing) {
        await api.patch(`/mail/groups/${editing.id}`, payload);
        toast.success('Group updated');
      } else {
        await api.post('/mail/groups', payload);
        toast.success('Group created');
      }
      setEditorOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/mail/groups/${toDelete.id}`);
      toast.success('Group deleted');
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  return (
    <div className="mt-4" data-testid="mail-groups-manager">
      <Card className="rounded-xl border bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--divider)]">
          <div className="flex items-center gap-2 min-w-0">
            <UsersRound size={16} className="text-[var(--diamond-icon)] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--ink-navy)]">My recipient groups</div>
              <div className="text-[11px] text-slate-500 truncate">Save a set of teammates once, then add them all to a message in one click. Groups are private to you.</div>
            </div>
          </div>
          <Button className="luxury-button h-9 shrink-0" onClick={openCreate} data-testid="mail-groups-new-button">
            <Plus size={15} className="mr-1.5" /> New group
          </Button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="space-y-3" data-testid="mail-groups-loading">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
            </div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="mail-groups-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--diamond-bg)]">
                <Users size={22} className="text-[var(--diamond-icon)]" />
              </div>
              <p className="mt-3 text-sm font-medium text-[var(--ink-navy)]">No groups yet</p>
              <p className="mt-1 max-w-sm text-[12.5px] text-slate-500">Create a group like &ldquo;Production Team&rdquo; or &ldquo;Sales Desk&rdquo; so you can message everyone at once without picking them one by one.</p>
              <Button variant="outline" size="sm" className="mt-4 h-9" onClick={openCreate} data-testid="mail-groups-empty-create">
                <Plus size={14} className="mr-1.5" /> Create your first group
              </Button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {groups.map((g) => (
                <div
                  key={g.id}
                  className="flex flex-col rounded-xl border border-[var(--divider)] bg-[var(--paper-warm)]/40 p-3.5 transition-shadow hover:shadow-sm"
                  data-testid={`mail-group-card-${g.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)] shrink-0">
                        <Users size={15} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-[var(--ink-navy)]" data-testid={`mail-group-name-${g.id}`}>{g.name}</div>
                        <div className="text-[11px] text-slate-500">{g.member_count} member{g.member_count === 1 ? '' : 's'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => openEdit(g)} className="rounded-md p-1.5 text-slate-500 hover:bg-white hover:text-[var(--ink-navy)]" title="Edit group" data-testid={`mail-group-edit-${g.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button type="button" onClick={() => setToDelete(g)} className="rounded-md p-1.5 text-slate-500 hover:bg-white hover:text-rose-600" title="Delete group" data-testid={`mail-group-delete-${g.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(g.members || []).slice(0, 6).map((m) => (
                      <Badge key={m.id} variant="secondary" className="border border-[var(--diamond-border)] bg-white text-[11px] font-normal text-slate-600" title={`${m.email} · ${ROLE_LABEL[m.role] || m.role}`}>
                        {m.name || m.email}
                      </Badge>
                    ))}
                    {(g.members || []).length > 6 && (
                      <Badge variant="secondary" className="bg-white text-[11px] font-normal text-slate-500">+{g.members.length - 6} more</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg" data-testid="mail-group-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit group' : 'New group'}</DialogTitle>
            <DialogDescription>Name the group and choose its members. Only you can see and use your groups.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Group name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Production Team"
                className="mt-1 h-9"
                data-testid="mail-group-name-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Members</Label>
              <div className="mt-1">
                <RecipientPicker selected={members} onChange={setMembers} showGroups={false} />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">{members.length} selected</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="luxury-button" onClick={save} disabled={saving} data-testid="mail-group-save-button">
              {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="mail-group-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this group?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{toDelete?.name}&rdquo; will be removed. This only deletes the group — the teammates themselves are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="mail-group-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MailGroupsManager;
